
// Pure placement maths for the LineString direction indicators (the small
// discreet chevrons that show which way a line runs: coordinates[0] →
// coordinates[n-1]). No DOM/THREE - Level-1 unit-testable. FeatureVisual.js
// feeds it the polyline already projected to screen space and turns each
// returned anchor into a billboarded chevron at the Z-correct 3D position.
//
// The markers are distributed by SCREEN-SPACE length, not one-per-segment:
// a long line gets a handful of evenly spaced chevrons, a line made of many
// tiny segments does not turn into a wall of arrows, and a line that is only
// a few pixels long on screen gets none (nothing meaningful to show).

function dist(a, b){
	return Math.hypot(b.x - a.x, b.y - a.y);
}

// screenPoints: [{x,y}] - one per polyline vertex, already projected to
//   canvas pixels (y-down), in vertex order (so [0] is the line's start).
// options:
//   spacingPx    - target gap between chevrons along the visible line
//   maxMarkers   - hard cap, so a very long line stays discreet
//   endMarginFrac- fraction of the visible length kept clear at each end
//                  (chevrons sitting exactly on the endpoints would collide
//                  with the endpoint markers)
//   minScreenLenPx - below this the line is treated as a dot: no chevrons
//
// Returns [{segmentIndex, t, angleRad}] in start→end order, where angleRad is
// the screen-space heading of travel at that anchor (atan2(dy,dx), y-down).
// Empty when the line is degenerate on screen (too short, or every segment
// projects to zero length).
export function planDirectionMarkers(screenPoints, options = {}){
	const {
		spacingPx = 100,
		maxMarkers = 10,
		endMarginFrac = 0.12,
		minScreenLenPx = 16,
	} = options;

	if(!Array.isArray(screenPoints) || screenPoints.length < 2) return [];

	const segCount = screenPoints.length - 1;
	const segLen = new Array(segCount);
	const cum = new Array(screenPoints.length);
	cum[0] = 0;
	for(let i = 0; i < segCount; i++){
		segLen[i] = dist(screenPoints[i], screenPoints[i + 1]);
		cum[i + 1] = cum[i] + segLen[i];
	}
	const total = cum[segCount];
	if(total < minScreenLenPx) return [];

	// heading at a given segment, falling back to the nearest segment that
	// actually has a direction when this one collapsed to a point on screen
	const headingAt = (seg) => {
		for(let step = 0; step < segCount; step++){
			for(const k of [seg + step, seg - step]){
				if(k < 0 || k >= segCount || segLen[k] < 1e-6) continue;
				const a = screenPoints[k], b = screenPoints[k + 1];
				return Math.atan2(b.y - a.y, b.x - a.x);
			}
		}
		return null;
	};

	const margin = total * endMarginFrac;
	const usable = total - 2 * margin;

	let distances;
	if(usable <= spacingPx * 0.5){
		distances = [total / 2]; // too short to space several - a single mid-line chevron
	}else{
		const count = Math.min(maxMarkers, Math.max(1, 1 + Math.floor(usable / spacingPx)));
		distances = count === 1
			? [total / 2]
			: Array.from({length: count}, (_, i) => margin + (i * usable) / (count - 1));
	}

	const markers = [];
	for(const d of distances){
		// segment containing arc-distance d
		let seg = 0;
		while(seg < segCount - 1 && cum[seg + 1] < d) seg++;
		const t = segLen[seg] > 1e-6 ? Math.min(1, Math.max(0, (d - cum[seg]) / segLen[seg])) : 0;
		const angleRad = headingAt(seg);
		if(angleRad === null) continue; // whole line projects to a point
		markers.push({segmentIndex: seg, t, angleRad});
	}
	return markers;
}
